import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from '@/components/ui/icon'
import brazilFlag from '@/assets/icons/brazil-flag.svg'
import usaFlag from '@/assets/icons/usa-flag.svg'

const languages = [
  { code: 'pt-BR', label: 'PT-BR', flag: brazilFlag },
  { code: 'en', label: 'EN', flag: usaFlag },
]

export default function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const current = languages.find((l) => l.code === i18n.resolvedLanguage) ?? languages[0]

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-on-surface-variant hover:text-primary transition-colors duration-300 cursor-pointer"
      >
        <img
          src={current.flag}
          alt={current.label}
          className="h-3.5 w-5 object-cover rounded-[2px] shadow-sm"
        />
        <span className="font-display text-label-sm font-medium tracking-wider">
          {current.label}
        </span>
        <Icon
          name="expand_more"
          className={`text-sm opacity-60 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <div
        className={`absolute right-0 top-full mt-2 min-w-[120px] bg-surface-container-high border border-outline-variant/15 shadow-xl shadow-black/20 overflow-hidden transition-all duration-300 origin-top ${
          open
            ? 'opacity-100 scale-y-100 pointer-events-auto'
            : 'opacity-0 scale-y-90 pointer-events-none'
        }`}
      >
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => {
              i18n.changeLanguage(lang.code)
              setOpen(false)
            }}
            className={`w-full px-4 py-3 text-left font-display text-label-sm font-medium tracking-wider transition-colors duration-200 cursor-pointer flex items-center justify-between gap-3 ${
              lang.code === i18n.resolvedLanguage
                ? 'text-primary bg-primary/8'
                : 'text-on-surface-variant hover:text-primary hover:bg-surface-container-highest'
            }`}
          >
            <span className="flex items-center gap-2.5">
              <img
                src={lang.flag}
                alt={lang.label}
                className="h-3.5 w-5 object-cover rounded-[2px] shadow-sm"
              />
              {lang.label}
            </span>
            {lang.code === i18n.resolvedLanguage && (
              <Icon name="check" className="text-sm text-primary" />
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
